import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getDeploymentDetail, rollbackDeployment } from '@/api/deploy'
import type { DeploymentDetail, DeploymentLogEntry } from '@/types'

const STATUS_COLORS: Record<string, string> = {
  completed: '#22c55e',
  failed: '#ef4444',
  rolled_back: '#f97316',
  in_progress: '#3b82f6',
  pending: '#6b7280',
  success: '#22c55e',
  warning: '#f59e0b',
  skipped: '#a3a3a3',
  deploying_db: '#3b82f6',
  generating_xml: '#8b5cf6',
  deploying_xml: '#06b6d4',
}

const STEP_LABELS: Record<string, string> = {
  read_model: 'Read Transaction Model',
  git_operations: 'Git Operations',
  db_connect: 'Connect to Database',
  backup_tables: 'Backup Navigation Tables',
  enhance_model: 'Enhance Model',
  deploy_db: 'Deploy Runtime Configuration',
  generate_xml: 'Generate XML Files',
  deploy_xml: 'Deploy XML to App Server',
  deploy_report: 'Deploy Report Files to App Server',
  clear_cache: 'Clear App Server Cache',
  db_commit: 'Commit Transaction',
  error: 'Error',
}

function StatusBadge({ status, large }: { status: string; large?: boolean }) {
  const color = STATUS_COLORS[status] || '#6b7280'
  return (
    <span
      style={{
        display: 'inline-block',
        padding: large ? '4px 16px' : '2px 10px',
        borderRadius: 12,
        fontSize: large ? 14 : 12,
        fontWeight: 600,
        color: '#fff',
        background: color,
        textTransform: 'capitalize',
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function StepIcon({ status }: { status: string }) {
  const icons: Record<string, string> = {
    success: '\u2705',
    warning: '\u26A0\uFE0F',
    failed: '\u274C',
    skipped: '\u23ED\uFE0F',
    in_progress: '\u23F3',
    pending: '\u23F8\uFE0F',
  }
  return <span style={{ fontSize: 17, marginRight: 8 }}>{icons[status] || '\u2B55'}</span>
}

export default function DeploymentDetailPage() {
  const { project, deploymentId } = useParams<{ project: string; deploymentId: string }>()
  const navigate = useNavigate()
  const projectId = decodeURIComponent(project || '')

  const [detail, setDetail] = useState<DeploymentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [expandedStep, setExpandedStep] = useState<number | null>(null)

  useEffect(() => {
    if (!deploymentId) return
    setLoading(true)
    getDeploymentDetail(Number(deploymentId))
      .then(setDetail)
      .catch(() => toast.error('Failed to load deployment details'))
      .finally(() => setLoading(false))
  }, [deploymentId])

  const handleRollback = async () => {
    if (!detail || !confirm('Are you sure you want to rollback this deployment?')) return
    try {
      const result = await rollbackDeployment(detail.id)
      setDetail({ ...detail, ...result, logs: detail.logs })
      toast.success('Deployment rolled back')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Rollback failed'
      toast.error(msg)
    }
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    if (seconds < 60) return `${seconds}s`
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  }

  const formatMs = (ms: number | null) => {
    if (!ms && ms !== 0) return '-'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString()
  }

  if (loading) {
    return (
      <div style={{ padding: '24px 32px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
        Loading deployment details...
      </div>
    )
  }

  if (!detail) {
    return (
      <div style={{ padding: '24px 32px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
        Deployment not found
      </div>
    )
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1000 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => navigate(`/deploy/${encodeURIComponent(projectId)}`)}
          className="btn-ghost"
          style={{ padding: '4px 8px', fontSize: 14 }}
        >
          &larr; Back to Deploy
        </button>
        <h1 style={{ fontSize: 23, fontWeight: 600, margin: 0, flex: 1 }}>
          {detail.version_tag || `Deployment #${detail.id}`}
        </h1>
        <StatusBadge status={detail.status} large />
        {detail.can_rollback && detail.status === 'completed' && (
          <button className="btn-secondary" onClick={handleRollback} style={{ fontSize: 14 }}>
            Rollback
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <SummaryCard label="Object" value={`${detail.obj_name} (${detail.obj_type_label || detail.obj_type || 'Transaction'})`} />
        <SummaryCard label="Duration" value={formatDuration(detail.duration_seconds)} />
        <SummaryCard label="Tables Affected" value={String(detail.tables_affected)} />
        <SummaryCard label="XMLs Generated" value={String(detail.xml_files_generated)} />
        <SummaryCard label="XMLs Deployed" value={String(detail.xml_files_deployed)} />
      </div>

      {/* Git & DB Info */}
      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Deployment Info</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 14 }}>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Started:</span>{' '}
            {formatDate(detail.started_at)}
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Completed:</span>{' '}
            {formatDate(detail.completed_at)}
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Git Commit:</span>{' '}
            <code style={{ fontSize: 13 }}>{detail.git_commit_hash?.substring(0, 7) || '-'}</code>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Branch:</span>{' '}
            {detail.git_branch || '-'}
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Target DB:</span>{' '}
            {detail.target_db_vendor || '-'} @ {detail.target_db_host || '-'} / {detail.target_db_name || '-'}
          </div>
          {detail.rolled_back_at && (
            <div>
              <span style={{ color: 'var(--color-text-secondary)' }}>Rolled back:</span>{' '}
              {formatDate(detail.rolled_back_at)}
            </div>
          )}
        </div>

        {detail.error_message && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.2)',
              fontSize: 14,
              color: '#ef4444',
            }}
          >
            <strong>Error:</strong> {detail.error_message}
          </div>
        )}
      </div>

      {/* Step-by-step Logs */}
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Deployment Steps</h3>

        {detail.logs.length === 0 ? (
          <div style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>No log entries</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {detail.logs.map((log: DeploymentLogEntry, idx: number) => (
              <div key={log.id}>
                {/* Step row */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 0',
                    cursor: log.detail ? 'pointer' : 'default',
                    borderBottom:
                      idx < detail.logs.length - 1
                        ? '1px solid var(--color-border)'
                        : 'none',
                  }}
                  onClick={() => {
                    if (log.detail) {
                      setExpandedStep(expandedStep === log.id ? null : log.id)
                    }
                  }}
                >
                  <StepIcon status={log.status} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      {STEP_LABELS[log.step_name] || log.step_name}
                    </div>
                    {log.message && (
                      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        {log.message}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginLeft: 12 }}>
                    {formatMs(log.duration_ms)}
                  </div>
                  <StatusBadge status={log.status} />
                </div>

                {/* Expandable detail */}
                {expandedStep === log.id && log.detail && (
                  <div
                    style={{
                      padding: '8px 16px',
                      marginBottom: 8,
                      background: 'var(--color-bg-secondary, rgba(0,0,0,0.03))',
                      borderRadius: 4,
                      fontSize: 13,
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      overflowX: 'auto',
                      maxHeight: 400,
                      overflow: 'auto',
                      lineHeight: 1.7,
                    }}
                  >
                    {log.detail.split('\n').map((line, li) => {
                      if (line.startsWith('>> ')) {
                        return (
                          <div key={li} style={{ color: 'var(--color-accent)', fontWeight: 600 }}>
                            {line}
                          </div>
                        )
                      }
                      if (line.startsWith('   ')) {
                        return (
                          <div key={li} style={{ color: 'var(--color-text-secondary)' }}>
                            {line}
                          </div>
                        )
                      }
                      if (line.startsWith('Failed at:') || line.startsWith('Table(s):')) {
                        return (
                          <div key={li} style={{ color: '#ef4444', fontWeight: 600 }}>
                            {line}
                          </div>
                        )
                      }
                      if (line.startsWith('Completed steps:')) {
                        return (
                          <div key={li} style={{ color: 'var(--color-text-secondary)', fontWeight: 600, marginBottom: 4 }}>
                            {line}
                          </div>
                        )
                      }
                      return <div key={li}>{line}</div>
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="card"
      style={{
        padding: '12px 16px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 19, fontWeight: 600 }}>{value}</div>
    </div>
  )
}
